import { ClerkProvider, useAuth } from "@clerk/clerk-expo";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import * as SentryNative from "@sentry/react-native";
import { Stack, usePathname } from "expo-router";
import Head from "expo-router/head";
import { ThemeProvider as NavigationThemeProvider } from "expo-router/react-navigation";
import * as SecureStore from "expo-secure-store";
import { StatusBar } from "expo-status-bar";
import type React from "react";
import { memo, useMemo } from "react";
import { LogBox, useColorScheme } from "react-native";
import { PaperProvider } from "react-native-paper";
import type { ThemeProp } from "react-native-paper/lib/typescript/types";
import {
	ReanimatedLogLevel,
	configureReanimatedLogger,
} from "react-native-reanimated";

import theme from "@configs/theme";
import { ThemeContextProvider, useThemeContext } from "@contexts/ThemeContext";
import {
	type ThemePreference,
	resolveIsDarkMode,
} from "@contexts/theme-preference";

import { buildType } from "@helpers/app-identity";
import { getClerkPublishableKey } from "@helpers/find-onboarding";
import { initSentry } from "@helpers/lib/sentry";
import { useAppStore } from "@hooks/useAppStore";
import useExams from "@hooks/useExams";
import { useLocale } from "@hooks/useLocale";
import { applyGlobalPolyfills } from "@memorang/helpers/applyGlobalPolyfills";
import { isWeb } from "@memorang/helpers/platform";
import { initializeAnalytics } from "analytics";
import useAppBootstrap from "../src/hooks/useAppBootstrap";

import { DialogProvider } from "@contexts/DialogContext/DialogProvider";
import { HapticProvider } from "@contexts/HapticContexts";
import { MigrationProvider } from "@contexts/MigrationContext";
import ReactQueryProvider from "@contexts/ReactQueryContext";
import {
	AuthProvider,
	useAuthContext,
} from "@features/auth/contexts/AuthContext";
import { ConsentProvider } from "@features/auth/contexts/ConsentProvider";
import { ExamContextProvider } from "@features/exam/contexts/ExamContext";
import { useExamBranding } from "@features/exam/hooks/useExamBranding";
import { getAuthToken } from "@helpers/auth-provider";
import { TTSProvider } from "@memorang/features/src/TTS/TTSContext";
import Box from "@memorang/ui/components/Box";
import { GestureHandlerRootHOC } from "@memorang/ui/components/GestureHandlerRootHOC";
import { ToastProvider } from "@memorang/ui/components/Toast";
import { AppConfigProvider } from "@memorang/ui/contexts/AppConfigContext";
import { AuthTokenProvider } from "@memorang/ui/contexts/AuthTokenContext";
import MaintenanceViewContainer from "components/MaintenanceViewContainer";
import VersionCheckerProvider from "./providers/VersionChecker";

import Loader from "@memorang/ui/components/Loader";

let hasBootstrapped = false;
const enableLogging = false;

const bootstrapApp = () => {
	if (hasBootstrapped) {
		return;
	}
	hasBootstrapped = true;

	applyGlobalPolyfills();
	initSentry();
	initializeAnalytics();

	configureReanimatedLogger({
		level: ReanimatedLogLevel.warn,
		strict: false,
	});

	if (!enableLogging) {
		LogBox.ignoreAllLogs();
	}
};

bootstrapApp();

const tokenCache = {
	getToken: async (key: string) => {
		try {
			return await SecureStore.getItemAsync(key);
		} catch (error) {
			console.error("SecureStore get item error:", error);

			try {
				await SecureStore.deleteItemAsync(key);
			} catch {
				// noop
			}

			return null;
		}
	},

	saveToken: async (key: string, value: string) => {
		try {
			await SecureStore.setItemAsync(key, value);
		} catch (error) {
			console.error("SecureStore set item error:", error);
		}
	},
};

const WebHead = () => {
	if (!isWeb) {
		return null;
	}

	return (
		<Head>
			<link
				rel="shortcut icon"
				href={`https://services.memorang.com/storage/v1/object/public/memorang-assets/icons/${buildType}/icon.png`}
			/>
			<style>{`
				body { cursor: default; }
				input, textarea, [contenteditable] { cursor: auto; }
			`}</style>
		</Head>
	);
};

const AppDataBootstrap = () => {
	useExams();

	return null;
};

const DefaultThemeProvider = ({
	themePreference,
	children,
}: {
	themePreference: ThemePreference;
	children: React.ReactNode;
}) => {
	const systemColorScheme = useColorScheme();
	const isDarkMode = resolveIsDarkMode(themePreference, systemColorScheme);

	return (
		<ThemeContextProvider initThemePreference={themePreference}>
			<PaperProvider theme={theme(isDarkMode) as unknown as ThemeProp}>
				{children}
			</PaperProvider>
		</ThemeContextProvider>
	);
};

const ClerkAuthRoot = ({ children }: { children: React.ReactNode }) => {
	const publishableKey = getClerkPublishableKey();
	return (
		<ClerkProvider tokenCache={tokenCache} publishableKey={publishableKey}>
			{children}
		</ClerkProvider>
	);
};

const AppConfigRoot = ({ children }: { children: React.ReactNode }) => {
	const avatarType = useAppStore((store) => store.app?.avatarType);
	const proficiencyLevels = useAppStore(
		(store) => store.app?.proficiencyLevels,
	);
	const masteryLevels = useAppStore((store) => store.app?.masteryLevels);
	const allowChangeExam = useAppStore((store) => store.exam.allowChangeExam);
	const ai = useAppStore((store) => store.sessionView?.ai);
	const maxScaledScore = useAppStore((store) => store.app?.maxScaledScore);
	const proficiencyTargetScore = useAppStore(
		(store) => store.app?.proficiencyTargetScore,
	);
	const reportView = useAppStore((store) => store.reportView);
	const hideTotalItemCounts = useAppStore(
		(store) => store.app?.hideTotalItemCounts,
	);
	const allowMarking = useAppStore(
		(store) => store.sessionView?.allowMarking ?? true,
	);
	const appFont = useAppStore((store) => store.app?.font);

	const appConfig = useMemo(
		() => ({
			buildType,
			avatarType: avatarType ?? "initials",
			allowChangeExam,
			masteryLevels,
			proficiencyLevels,
			proficiencyTargetScore,
			reportView: reportView ?? undefined,
			maxScaledScore,
			hideTotalItemCounts,
			allowMarking,
			ai,
			appFont,
		}),
		[
			avatarType,
			allowChangeExam,
			masteryLevels,
			proficiencyLevels,
			proficiencyTargetScore,
			reportView,
			maxScaledScore,
			hideTotalItemCounts,
			allowMarking,
			ai,
			appFont,
		],
	);

	return <AppConfigProvider value={appConfig}>{children}</AppConfigProvider>;
};

const FeatureProviders = ({ children }: { children: React.ReactNode }) => {
	const appId = useAppStore((store) => store.app?.id);
	const org = useAppStore((store) => store.companyName);
	const tenantId = useAppStore((store) => store.tenant?.id);
	const appName = useAppStore((store) => store.app?.name);

	return (
		<ToastProvider>
			<VersionCheckerProvider>
				<MigrationProvider>
					<HapticProvider>
						<TTSProvider
							org={org}
							tenantId={tenantId}
							appId={appId}
							appName={appName}
						>
							<BottomSheetModalProvider>
								<DialogProvider>{children}</DialogProvider>
							</BottomSheetModalProvider>
						</TTSProvider>
					</HapticProvider>
				</MigrationProvider>
			</VersionCheckerProvider>
		</ToastProvider>
	);
};

const ThemedShell = ({ children }: { children: React.ReactNode }) => {
	const { isDarkMode } = useThemeContext();
	const examBranding = useExamBranding();
	const finalTheme = theme(isDarkMode, examBranding?.theme);
	return (
		<PaperProvider theme={finalTheme as unknown as ThemeProp}>
			<NavigationThemeProvider
				value={{
					...finalTheme,
					fonts: {
						...finalTheme.typography.fonts,
						heavy: finalTheme.typography.fonts.extraBold,
						// biome-ignore lint/correctness/noUndeclaredVariables: <explanation>
					} as ReactNavigation.Theme["fonts"],
				}}
			>
				<StatusBar
					key={isDarkMode ? "dark" : "light"}
					style={isDarkMode ? "light" : "dark"}
				/>
				{children}
			</NavigationThemeProvider>
		</PaperProvider>
	);
};

const AppShell = () => {
	const { isLoaded: isClerkLoaded } = useAuth();
	const { isSignedIn } = useAuthContext();
	return (
		<ExamContextProvider>
			<ConsentProvider>
				<AuthTokenProvider value={getAuthToken}>
					<AppConfigRoot>
						<ThemedShell>
							<AppDataBootstrap />
							<Box flex={1}>
								{isClerkLoaded ? (
									<FeatureProviders>
										<Stack screenOptions={{ headerShown: false }}>
											<Stack.Protected guard={isSignedIn}>
												<Stack.Screen name="(protected)" />
											</Stack.Protected>
											<Stack.Screen name="index" />
											<Stack.Screen name="(auth)" />
										</Stack>
									</FeatureProviders>
								) : (
									<Loader isFullScreen />
								)}
							</Box>
						</ThemedShell>
					</AppConfigRoot>
				</AuthTokenProvider>
			</ConsentProvider>
		</ExamContextProvider>
	);
};

const AppEntry = memo(() => {
	const { isLoadingComplete, themePreference } = useAppBootstrap();
	useLocale();

	const appId = useAppStore((store) => store.app?.id);
	const maintenanceEnabled = useAppStore(
		(store) => store.app?.maintenance?.enabled,
	);

	if (!(appId && isLoadingComplete)) {
		return (
			<DefaultThemeProvider themePreference={themePreference}>
				<Loader isFullScreen />
			</DefaultThemeProvider>
		);
	}

	if (maintenanceEnabled) {
		return (
			<DefaultThemeProvider themePreference={themePreference}>
				<MaintenanceViewContainer />
			</DefaultThemeProvider>
		);
	}

	return (
		<ClerkAuthRoot>
			<GestureHandlerRootHOC>
				<ThemeContextProvider initThemePreference={themePreference}>
					<ReactQueryProvider>
						<AuthProvider app={buildType}>
							<AppShell />
						</AuthProvider>
					</ReactQueryProvider>
				</ThemeContextProvider>
			</GestureHandlerRootHOC>
		</ClerkAuthRoot>
	);
});

AppEntry.displayName = "AppEntry";

const AppRouter = () => {
	const pathname = usePathname();
	return (
		<>
			<WebHead />
			{pathname === "/health" ? (
				<Stack
					screenOptions={{
						headerShown: false,
					}}
				/>
			) : (
				<AppEntry />
			)}
		</>
	);
};

export default isWeb ? AppRouter : SentryNative.wrap(AppRouter);
